import { alpha, useTheme } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import {
  botMessageColors,
  botMessageColorsDark,
} from "../../../../colors/colors";

// Distinct pastel surfaces for up to three swipeable reference cards.
export const REFERENCE_CARD_PALETTES = [
  {
    bg: "#F6F0DC",
    bgDark: "#3A3628",
    border: "#E4D7B0",
    borderDark: "#6B6248",
  },
  {
    bg: "#E6F1F4",
    bgDark: "#243338",
    border: "#B7D0D9",
    borderDark: "#4A6570",
  },
  {
    bg: "#EAF3E8",
    bgDark: "#283528",
    border: "#B9D4B5",
    borderDark: "#4F6B4C",
  },
] as const;

export function useReferenceCardMessageStyles(cardIndex: number) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const palette =
    REFERENCE_CARD_PALETTES[cardIndex % REFERENCE_CARD_PALETTES.length];
  const timestampColor = (
    isDark ? botMessageColorsDark : botMessageColors
  ).default.timeColor;

  const cardBg = isDark ? palette.bgDark : palette.bg;
  const cardBorder = isDark ? palette.borderDark : palette.border;
  const textPrimary = isDark
    ? theme.palette.common.white
    : theme.palette.grey[900];
  const textMuted = isDark
    ? alpha(theme.palette.common.white, 0.65)
    : theme.palette.grey[600];

  const styles: Record<string, SxProps<Theme>> = {
    root: {
      width: "100%",
      maxWidth: "100%",
      display: "flex",
      flexDirection: "column",
      gap: 1,
    },
    swipeArea: {
      width: "100%",
      touchAction: "pan-y",
      userSelect: "none",
    },
    card: {
      width: "100%",
      boxSizing: "border-box",
      borderRadius: "16px",
      backgroundColor: cardBg,
      border: `1px solid ${cardBorder}`,
      px: { xs: 1.75, sm: 2.25 },
      pt: { xs: 1.75, sm: 2 },
      pb: { xs: 1.25, sm: 1.5 },
      display: "flex",
      flexDirection: "column",
      gap: 1.25,
      fontFamily: '"Red Hat Display", sans-serif',
      color: textPrimary,
    },
    headerRow: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 1,
    },
    eyebrow: {
      fontSize: "0.75rem",
      fontWeight: 500,
      lineHeight: 1.35,
      color: textMuted,
      letterSpacing: "0.01em",
    },
    pinIcon: {
      color: textMuted,
      fontSize: 18,
      flexShrink: 0,
      mt: "1px",
    },
    question: {
      fontSize: { xs: "0.95rem", sm: "1.05rem" },
      fontWeight: 700,
      lineHeight: 1.35,
      color: textPrimary,
      m: 0,
    },
    sourceIssue: {
      display: "flex",
      gap: 1,
      alignItems: "flex-start",
      borderRadius: "10px",
      border: `1px solid ${isDark ? "#B85C5C" : "#E8A0A0"}`,
      backgroundColor: isDark
        ? alpha("#F44336", 0.18)
        : alpha("#F44336", 0.08),
      px: 1.25,
      py: 1,
    },
    sourceIssueIcon: {
      color: isDark ? "#FF8A80" : "#D32F2F",
      fontSize: 20,
      mt: "1px",
      flexShrink: 0,
    },
    sourceIssueTitle: {
      fontSize: "0.8125rem",
      fontWeight: 700,
      color: isDark ? "#FF8A80" : "#C62828",
      lineHeight: 1.3,
      mb: 0.25,
    },
    sourceIssueMessage: {
      fontSize: "0.8125rem",
      fontWeight: 500,
      color: isDark ? "#FFCDD2" : "#8B3A3A",
      lineHeight: 1.4,
    },
    body: {
      fontSize: { xs: "0.875rem", sm: "0.9375rem" },
      lineHeight: 1.5,
      color: textPrimary,
      "& .markdown": { color: "inherit" },
      "& .markdown p": { margin: "0 0 0.65em" },
      "& .markdown p:last-child": { marginBottom: 0 },
      "& .markdown strong, & .markdown b": { fontWeight: 700 },
      "& .markdown ul, & .markdown ol": {
        margin: "0 0 0.65em",
        paddingLeft: "1.25em",
      },
    },
    reviewStatus: {
      fontSize: "0.7rem",
      fontWeight: 500,
      color: textMuted,
      lineHeight: 1.35,
    },
    readMoreRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      cursor: "pointer",
      mt: 0.25,
      py: 0.25,
      userSelect: "none",
      WebkitTapHighlightColor: "transparent",
      color: textPrimary,
    },
    readMoreLabel: {
      fontSize: "0.875rem",
      fontWeight: 600,
      color: "inherit",
    },
    galleryMeta: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 0.5,
      mt: 0.25,
      color: textPrimary,
    },
    footerRow: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      mt: 0.5,
    },
    timestamp: {
      fontSize: "12px",
      whiteSpace: "nowrap",
      color: timestampColor,
      fontFamily: '"Red Hat Display", sans-serif',
      fontWeight: 500,
    },
  };

  return { styles, cardBg };
}
